// Marketing plans for the brand page. The list is the #662 summary.
// The plan title links to /plans/:planId. That page loads
// GET /api/marketing/plans/:planId/detail.
// No accept, push, or pause actions on this card.

import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Badge, Button, Card, CardBody, HStack, Modal, ModalBody, ModalContent, ModalFooter,
  ModalHeader, ModalOverlay, Spinner, Text, VStack
} from '@chakra-ui/react';
import { listMarketingPlans, type PlanSummaryView } from '../../marketingPlans/client';
import {
  getDraftRuns, originLabel, planDraftsEnabled, requestPlanDraft,
  type DraftQuote, type DraftRunView
} from '../../marketingPlans/drafts';
import { planDetailHref, planHrefForLocation } from '../../marketingPlans/detail';
import { planPerformanceLink } from '../../marketingPlans/performance';

type ListState = {
  loading: boolean;
  plans: PlanSummaryView[];
  error: string | null;
};

export function PlansCard({ brandId }: { brandId: string }) {
  const { pathname } = useLocation();
  const draftsOn = planDraftsEnabled();
  const brandRef = useRef(brandId);
  brandRef.current = brandId;
  const [list, setList] = useState<ListState>({ loading: true, plans: [], error: null });
  const [reloadKey, setReloadKey] = useState(0);
  const [quote, setQuote] = useState<DraftQuote | null>(null);
  const [quotedFor, setQuotedFor] = useState<string | null>(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftNote, setDraftNote] = useState<string | null>(null);
  const [draftLocked, setDraftLocked] = useState(false);
  const [runs, setRuns] = useState<DraftRunView[]>([]);

  useEffect(() => {
    let cancelled = false;
    setList({ loading: true, plans: [], error: null });
    void listMarketingPlans(brandId).then((result) => {
      if (cancelled) return;
      setList({ loading: false, plans: result.plans, error: result.error });
    });
    return () => { cancelled = true; };
  }, [brandId, reloadKey]);

  useEffect(() => {
    if (!draftsOn) return;
    let cancelled = false;
    void getDraftRuns(brandId).then((next) => {
      if (!cancelled) setRuns(next);
    });
    return () => { cancelled = true; };
  }, [draftsOn, brandId, reloadKey]);

  useEffect(() => {
    setQuote(null);
    setQuotedFor(null);
    setQuoteOpen(false);
    setDraftNote(null);
    setDraftLocked(false);
  }, [brandId]);

  const ask = async () => {
    const forBrand = brandId;
    setDrafting(true);
    setDraftNote(null);
    const result = await requestPlanDraft(forBrand, false);
    if (brandRef.current !== forBrand) {
      setDrafting(false);
      return;
    }
    setDrafting(false);
    if (result.missing || !result.quote) {
      setDraftNote("New plans aren't available yet.");
      setQuote(null);
      setQuotedFor(null);
      return;
    }
    setQuotedFor(forBrand);
    setQuote(result.quote);
    setQuoteOpen(true);
  };

  const confirm = async () => {
    if (draftLocked || drafting) return;
    if (!quote || quote.overCap || quote.closed != null || quote.blockedReason || !quote.dailyCap || !quote.estimate) return;
    if (!quotedFor || quotedFor !== brandRef.current) return;
    const forBrand = quotedFor;
    setDrafting(true);
    const result = await requestPlanDraft(forBrand, true);
    if (brandRef.current !== forBrand) {
      setDrafting(false);
      return;
    }
    setDrafting(false);
    setQuoteOpen(false);
    if (result.created) {
      setDraftLocked(true);
      setDraftNote(null);
      setReloadKey((key) => key + 1);
      return;
    }
    if (result.quote?.blockedReason) setDraftNote(result.quote.blockedReason);
  };

  return (
    <Card variant="outline" data-testid="plans-section">
      <CardBody>
        <HStack justify="space-between" mb={3}>
          <Text fontWeight="700" color="brand.ink">Plans</Text>
          {draftsOn && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => { void ask(); }}
              isDisabled={draftLocked}
              isLoading={drafting}
              data-testid="new-plan"
            >
              New plan
            </Button>
          )}
        </HStack>
        {draftNote && <Text fontSize="sm" color="brand.muted" mb={3} data-testid="draft-note">{draftNote}</Text>}
        {list.loading && (
          <HStack>
            <Spinner size="sm" />
            <Text fontSize="sm" color="brand.muted">Loading plans…</Text>
          </HStack>
        )}
        {!list.loading && list.error && (
          <Text fontSize="sm" color="red.600" data-testid="plans-error">{list.error}</Text>
        )}
        {!list.loading && !list.error && list.plans.length === 0 && (
          <Text fontSize="sm" color="brand.muted" data-testid="plans-empty">No marketing plans yet.</Text>
        )}
        {!list.loading && !list.error && list.plans.length > 0 && (
          <VStack align="stretch" spacing={2}>
            {list.plans.map((plan) => (
              <HStack key={plan.id} align="stretch" spacing={2}>
                <Button
                  as={RouterLink}
                  to={planHrefForLocation(planDetailHref(brandId, plan.id, plan.revision), pathname)}
                  flex="1"
                  variant="outline"
                  justifyContent="flex-start"
                  h="auto"
                  py={3}
                  px={3}
                  data-testid={`plan-row-${plan.id}`}
                  aria-label={plan.title}
                >
                  <PlanSummary plan={plan} showDraft={draftsOn} />
                </Button>
                {planPerformanceLink(plan.status, brandId, plan.id, plan.revision) && (
                  <Button
                    as={RouterLink}
                    to={planHrefForLocation(
                      planPerformanceLink(plan.status, brandId, plan.id, plan.revision) ?? '',
                      pathname
                    )}
                    variant="outline"
                    alignSelf="center"
                    size="sm"
                    data-testid={`plan-performance-${plan.id}`}
                    aria-label={`Performance for ${plan.title}`}
                  >
                    Performance
                  </Button>
                )}
              </HStack>
            ))}
          </VStack>
        )}
        {draftsOn && runs.length > 0 && (
          <VStack align="stretch" spacing={1} mt={4} data-testid="draft-runs">
            <Text fontSize="xs" fontWeight="700" color="brand.muted" textTransform="uppercase">Draft runs</Text>
            {runs.map((run, index) => (
              <Text key={`${run.summary}-${index}`} fontSize="sm">
                {[run.at, run.summary, run.spend, run.resultingPlanId ? `Plan ${run.resultingPlanId}` : null].filter(Boolean).join(' · ')}
              </Text>
            ))}
          </VStack>
        )}
      </CardBody>

      <Modal isOpen={quoteOpen} onClose={() => setQuoteOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent data-testid="draft-quote">
          <ModalHeader>Ask the planning agent for a plan</ModalHeader>
          <ModalBody>
            <VStack align="stretch" spacing={2}>
              <Text fontSize="sm">Estimate {quote?.estimate ?? 'Not available'}</Text>
              <Text fontSize="sm">Daily cap {quote?.dailyCap ?? 'Not available'}</Text>
              <Text fontSize="sm">Spent today {quote?.spentToday ?? 'Not available'}</Text>
              {quote?.blockedReason && <Text fontSize="sm" data-testid="draft-blocked">{quote.blockedReason}</Text>}
              <Text fontSize="xs" color="brand.muted">Confirming creates a proposed plan. It does not push anything to Meta.</Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setQuoteOpen(false)}>Cancel</Button>
            <Button
              variant="brand"
              onClick={() => { void confirm(); }}
              isDisabled={!quote || quote.overCap || quote.closed != null || Boolean(quote.blockedReason) || !quote.estimate || !quote.dailyCap || quotedFor !== brandId}
              isLoading={drafting}
              data-testid="confirm-draft"
            >
              Draft plan
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
}

function PlanSummary({ plan, showDraft }: { plan: PlanSummaryView; showDraft: boolean }) {
  const drafted = showDraft ? originLabel(plan.origin) : null;
  return (
    <VStack align="stretch" spacing={1} w="100%" textAlign="left">
      <HStack spacing={2} align="center">
        <Text fontSize="sm" fontWeight="700" color="brand.ink">{plan.title}</Text>
        {plan.status && <Badge colorScheme={statusColor(plan.status)}>{plan.status}</Badge>}
      </HStack>
      <HStack spacing={3} fontSize="xs" color="brand.muted" flexWrap="wrap">
        <Text>{plan.revision ? `Revision ${plan.revision}` : 'Revision —'}</Text>
        <Text>{plan.weeklyBudget ? `Weekly budget ${plan.weeklyBudget}` : 'Weekly budget —'}</Text>
        <Text>{plan.updatedLabel ? `Updated ${plan.updatedLabel}` : 'Updated —'}</Text>
        {plan.acceptedLabel && <Text>{plan.acceptedLabel}</Text>}
        {drafted && <Text>{drafted}</Text>}
      </HStack>
      {showDraft && plan.draftReason && <Text fontSize="xs" color="brand.muted">{plan.draftReason}</Text>}
    </VStack>
  );
}

function statusColor(status: string | null): string {
  const value = (status ?? '').toLowerCase();
  if (value === 'accepted' || value === 'active' || value === 'live') return 'green';
  if (value === 'proposed' || value === 'draft') return 'purple';
  if (value === 'paused') return 'yellow';
  if (value === 'rejected' || value === 'archived') return 'red';
  return 'gray';
}
